import { z } from "zod";

export const CacheStatus = z.enum(["HIT", "MISS", "STALE", "BYPASS", "REVALIDATED"]);
export type CacheStatus = z.infer<typeof CacheStatus>;

/**
 * Append-only usage event emitted by the edge for every billable query.
 * queryId is the idempotency key for settlement: the ledger applies each queryId at most once.
 */
export const QueryUsageEvent = z.object({
  queryId: z.string(),
  idempotencyKey: z.string().max(128).nullable(),
  leaseId: z.string(),
  consumerOrgId: z.string(),
  apiKeyId: z.string(),
  providerOrgId: z.string(),
  datasetId: z.string(),
  capability: z.string(),
  ts: z.string(),
  priceMicros: z.number().int().nonnegative(),
  feeMicros: z.number().int().nonnegative(),
  providerMicros: z.number().int().nonnegative(),
  servingCostMicros: z.number().int().nonnegative(),
  cacheStatus: CacheStatus,
  latencyMs: z.number().int().nonnegative(),
  bytes: z.number().int().nonnegative(),
  rows: z.number().int().nonnegative(),
  status: z.enum(["ok", "error", "rejected"]),
  routingReason: z.string().max(200),
  colo: z.string().max(8).optional(),
});
export type QueryUsageEvent = z.infer<typeof QueryUsageEvent>;

export const LedgerEntryType = z.enum([
  "credit_purchase", // consumer funds added (from payment provider webhook)
  "promotional_credit", // DBIndex-funded developer credits (platform:marketing:credits → consumer balance)
  "query_capture", // consumer debit for a settled query
  "provider_accrual", // provider_pending credit
  "marketplace_fee",
  "provider_release", // pending -> available after hold period
  "provider_payout", // available -> paid
  "refund",
  "adjustment",
]);
export type LedgerEntryType = z.infer<typeof LedgerEntryType>;

/** Double-entry: every posting has balanced legs (sum of amounts across accounts == 0). */
export const LedgerAccount = z.string().regex(/^(consumer|provider|platform|external):[a-z_]+:[A-Za-z0-9_]+$/);
export const LedgerPosting = z.object({ account: LedgerAccount, amountMicros: z.number().int() });
export const LedgerTransaction = z.object({
  txId: z.string(),
  idempotencyKey: z.string(),
  type: LedgerEntryType,
  ts: z.string(),
  postings: z.array(LedgerPosting).min(2),
  ref: z.record(z.string(), z.string()).default({}),
});
export type LedgerTransaction = z.infer<typeof LedgerTransaction>;

export function assertBalanced(tx: { postings: { amountMicros: number }[] }): void {
  const sum = tx.postings.reduce((s, p) => s + p.amountMicros, 0);
  if (sum !== 0) throw new Error(`unbalanced ledger transaction (sum=${sum})`);
  for (const p of tx.postings) if (!Number.isSafeInteger(p.amountMicros)) throw new Error("non-integer posting");
}

/** Build the balanced postings for a settled query. */
export function queryCaptureTransaction(e: QueryUsageEvent): Omit<LedgerTransaction, "txId" | "ts"> {
  if (e.feeMicros + e.providerMicros !== e.priceMicros) throw new Error("fee + provider must equal price");
  return {
    idempotencyKey: `query:${e.queryId}`,
    type: "query_capture",
    postings: [
      { account: `consumer:balance:${e.consumerOrgId}`, amountMicros: -e.priceMicros },
      { account: `provider:pending:${e.providerOrgId}`, amountMicros: e.providerMicros },
      { account: `platform:fees:marketplace`, amountMicros: e.feeMicros },
    ],
    ref: { queryId: e.queryId, datasetId: e.datasetId, leaseId: e.leaseId },
  };
}
